// App.js

import { jwtDecode } from "jwt-decode";
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";
import Button from "react-bootstrap/Button";
import { Container } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import axios from "axios";
import { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import zedLogo from "./zedlogo.png";
import titleZedLogo from "./title-zedlogo.png";
import { useAuth, hasAuthParams } from "react-oidc-context";
import UserProfileModal from "./UserProfileModal";

function App() {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [idToken, setIdToken] = useState();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [refreshToken, setRefreshToken] = useState();
  const [error, setError] = useState();
  const [showError, setShowError] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [displayFirstName, setDisplayFirstName] = useState("");
  const [displayLastName, setDisplayLastName] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const [profileLink, setProfileLink] = useState("");

  const verify = (clinic) => {
    if (clinic) {
      const instance = axios.create();
      instance.defaults.timeout = 60000;
      instance
        .post(
          `${process.env.REACT_APP_LAMBDA_URI}`,
          {
            clinic: clinic.siteName,
            id_token: idToken,
            refresh_token: refreshToken,
          },
          {
            headers: {
              Authorization: `${idToken}`,
            },
          },
        )
        .then((response) => {
          if (response.status === 200 && response.data.RedirectUrl) {
            window.location.href = response.data.RedirectUrl;
          } else {
            setError(`Unable to verify clinic ${clinic.displayName}`);
            setShowError(true);
          }
        })
        .catch((err) => {
          setError(`Unable to verify clinic ${clinic.displayName}`);
          setShowError(true);
        });
    }
  };

  const handleClinicSelect = (clinic) => {
    verify(clinic);
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const verifyAction = params.get("action");
      const verifyEmailParam = params.get("email");
      if (verifyAction === "verify-email" && verifyEmailParam) {
        localStorage.setItem("pendingEmailVerification", verifyEmailParam);
        window.location.href = "/Login";
        return;
      }
      const pendingEmail = localStorage.getItem("pendingEmailVerification");
      if (pendingEmail) {
        if (auth.isLoading) return;
        if (auth.error) {
          localStorage.removeItem("pendingEmailVerification");
          window.location.href = "/Login?error=email-verify-failed";
          return;
        }
        if (auth.isAuthenticated) {
          localStorage.removeItem("pendingEmailVerification");
          updateEmail(pendingEmail);
          // fall through to normal authenticated flow
        } else if (!hasAuthParams()) {
          localStorage.removeItem("pendingEmailVerification");
          window.location.href = "/Login";
          return;
        } else {
          return;
        }
      }

      const code = params.get("code");
      if (
        !hasAuthParams() &&
        !auth.isAuthenticated &&
        !auth.isLoading &&
        !hasTriedSignin &&
        code
      ) {
        setHasTriedSignin(true);
        auth.signinRedirect();
      } else if (
        !auth.isAuthenticated &&
        !auth.isLoading &&
        (!hasAuthParams() || auth.error)
      ) {
        window.location.href = "/Login";
      }

      let id_token;
      let refresh_token;
      if (auth.user?.id_token) {
        id_token = jwtDecode(auth.user?.id_token);
        refresh_token = auth.user?.refresh_token;
      } else {
        return;
      }

      if (!id_token || !refresh_token) {
        window.location.href = "/Login";
      }

      setIdToken(auth.user?.id_token);
      setRefreshToken(auth.user?.refresh_token);
      setDisplayFirstName(auth.user?.profile?.given_name || "");
      setDisplayLastName(auth.user?.profile?.family_name || "");

      const email = auth.user?.profile?.email ?? "";
      const zcloudUrl = process.env.REACT_APP_ZCLOUD_USER_URI;
      setShowChangePassword(email.includes(zcloudUrl));

      let clinics = [];

      if (id_token["custom:sites"]) {
        clinics = JSON.parse(id_token["custom:sites"]);
      }

      if (!clinics || clinics.length === 0) {
        clinics = id_token["cognito:groups"] || [];
      }

      if (!clinics || clinics.length === 0) {
        window.location.href = "/Login";
      }

      const instance = axios.create();
      instance.defaults.timeout = 60000;

      instance
        .post(`${process.env.REACT_APP_CLINIC_DISPLAY_NAMES_URI}`, {
          sitelist: clinics,
        })
        .then((response) => {
          if (response.status === 200 && response.data.siteDetails) {
            setClinics(response.data.siteDetails);
            setShowError(false);
          } else {
            window.location.href = "/Login";
          }
        })
        .catch((err) => {
          window.location.href = "/Login";
        });
    } catch (e) {
      window.location.href = "/Login";
    }
  }, [auth]);

  const handleLogout = () => {
    const domain = process.env.REACT_APP_LOGIN_REDIRECT_URI;
    const clientId = process.env.REACT_APP_LOGIN_CLIENT_ID;

    setIdToken(null);
    setRefreshToken(null);
    setClinics([]);

    auth.signoutRedirect({
      extraQueryParams: {
        client_id: clientId,
        logout_uri: `${domain}/login`,
      },
    });
  };

  const handleChangePassword = () => {
    window.location.href = process.env.REACT_APP_CHANGE_PASSWORD_URI;
  };

  const handleProfileSave = ({ firstName, lastName }) => {
    setDisplayFirstName(firstName);
    setDisplayLastName(lastName);
  };

  const handleEmailSaved = (message, link) => {
    setProfileMessage(message);
    setProfileLink(link || "");
  };

  const updateEmail = async (verifiedEmail) => {
    if (!process.env.REACT_APP_UPDATE_EMAIL_LAMBDA_URI) return;
    try {
      const instance = axios.create();
      instance.defaults.timeout = 60000;
      await instance.post(
        `${process.env.REACT_APP_UPDATE_EMAIL_LAMBDA_URI}`,
        { email: verifiedEmail },
        { headers: { Authorization: auth.user?.id_token } },
      );
    } catch (e) {
      console.error("Failed to update email:", e);
    }
  };

  return (
    <>
      {showError && (
        <div className="m-5">
          <Container>
            <Row></Row>
            <Row>
              <Col></Col>
              <Col className="d-flex justify-content-center mt-10">
                <Alert variant="danger">{error}</Alert>
              </Col>
              <Col></Col>
            </Row>
            <Row>
              <Col></Col>
              <Col className="d-flex justify-content-center">
                <Card.Link href="/login">Login</Card.Link>
              </Col>
              <Col></Col>
            </Row>
          </Container>
        </div>
      )}

      {!showError && (
        <div>
          {profileMessage && (
            <Alert
              variant="info"
              dismissible
              onClose={() => {
                setProfileMessage("");
                setProfileLink("");
              }}
              className="m-3"
            >
              {profileMessage}
              {profileLink && (
                <div className="mt-2">
                  <a href={profileLink}>{profileLink}</a>
                </div>
              )}
            </Alert>
          )}
          <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center p-3 gap-2">
            <span>
              {displayFirstName && displayLastName
                ? `Hi ${displayFirstName} ${displayLastName}`
                : "Please update your profile"}
            </span>
            <Button
              variant="secondary"
              onClick={() => setShowProfileModal(true)}
            >
              My Profile
            </Button>
            {showChangePassword && (
              <Button variant="secondary" onClick={handleChangePassword}>
                Change Password
              </Button>
            )}
            <Button variant="danger" onClick={handleLogout}>
              Logout
            </Button>
          </div>
          <div className="container text-center p-4">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "10px",
              }}
            >
              <img
                src={titleZedLogo}
                alt="logo"
                className="img-fluid"
                style={{ maxWidth: "475px", width: "100%", height: "auto" }}
              />
              <h1></h1>
            </div>
            <Alert variant="info">
              Select a clinic from the list to log in
            </Alert>
            <div className="row justify-content-center">
              {clinics.map((clinic, index) => {
                return (
                  <div key={index} className="col-sm-12 col-lg-2">
                    <Card
                      className="m-2"
                      style={{ width: "auto", height: "10rem" }}
                      key={index}
                      onClick={() => handleClinicSelect(clinic)}
                    >
                      <Button
                        variant="outline-primary"
                        className="w-100 h-100"
                        style={{ border: "black" }}
                      >
                        <div
                          style={{
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            style={{ width: 60, height: 60 }}
                            src={zedLogo}
                            alt="fireSpot"
                          />
                          <p>{clinic.displayName}</p>
                        </div>
                      </Button>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <UserProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        onSave={handleProfileSave}
        onEmailSaved={handleEmailSaved}
      />
    </>
  );
}

export default App;
